import sortBy from 'lodash/sortBy'

import { getApi } from '../api'
import { useMemo } from 'react'
import { useGetUserQuery } from '../services/useUser'
import { useGetCompaniesQuery } from '../services/useCompany'
import { filterVisibleSubscriptions } from '../utils/subscriptionUtils'
import { CompanySubscription, Company } from '../types/responses'

// Extended subscription type that includes company information
export type ExtendedSubscription = CompanySubscription & {
  chargebee_id: string | null
  company: Company
  company_id: number
  company_name: string
  display_name: string
  is_partner_customer: boolean
}

export const useSubscriptions = () => {
  const token = getApi().getToken?.()
  const { data: user } = useGetUserQuery({}, { skip: !!token })
  const { data: companies } = useGetCompaniesQuery(
    {},
    { skip: !user || !!token },
  )

  const subscriptions = useMemo(() => {
    if (!companies) return undefined

    const data = companies.flatMap((company) => {
      // Filter out addon-only subscriptions when main subscriptions exist
      const visibleSubscriptions = filterVisibleSubscriptions(
        company.subscriptions,
      )

      return visibleSubscriptions.map((v) => {
        const subscription = company.subscriptions?.find(
          (companySubscription) => v.id === companySubscription.id,
        )
        const name = `${subscription?.company_name || company.name} - ${v.name}`
        const is_partner_customer = subscription?.chargebee_id === null
        const extendedSubscription: ExtendedSubscription = {
          ...v,
          chargebee_id: company.chargebee_id,
          company,
          company_id: company.id,
          company_name: `${subscription?.company_name || company.name}`,
          display_name: `${name} ${v.is_cancelled ? ' (Cancelled)' : ''}`,
          is_partner_customer,
        }
        return extendedSubscription
      })
    })

    return sortBy(data, (sub) => {
      return `${sub.is_cancelled ? 1 : 0}${sub.display_name}`
    })
  }, [companies])

  return subscriptions
}

export default useSubscriptions
